import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Search, User, LogOut, Package, Calendar, Wallet, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CartSidebar from '../cart/CartSidebar';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { fetchCategories } from '@/services/categoryService';
import { Category } from '@/types/category';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '../LanguageSwitcher';
import ProductsMegaMenu from './ProductsMegaMenu';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(88);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const { cartItems } = useCart();
  const { user, signOut, isAdmin } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const { t, tl } = useLanguage();
  const location = useLocation();

  const isHome = location.pathname === '/';
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setCategories(await fetchCategories());
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const threshold = isHome ? Math.max(window.innerHeight * 0.75, 400) : 24;
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isHome]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductsDropdownOpen(false);
    setIsMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMenuOpen, isMegaOpen, scrolled]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProductsDropdownOpen(false);
    setIsMegaOpen(false);
  };

  const linkClass =
    'text-[11px] md:text-xs lg:text-sm font-semibold tracking-[0.12em] uppercase whitespace-nowrap text-foreground transition-colors hover:text-foreground/55';

  const iconBtnClass =
    '!bg-transparent shadow-none text-foreground hover:text-foreground/55 hover:!bg-muted';

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 border-0 border-none bg-white shadow-none"
      style={{ backgroundColor: '#ffffff', boxShadow: 'none', borderBottom: 'none' }}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Desktop Minotti-style bar */}
        <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] items-center gap-6 py-5">
          {/* Left links */}
          <nav className="flex items-center justify-end gap-6 xl:gap-8">
            <ProductsMegaMenu
              categories={categories}
              open={isMegaOpen}
              onOpenChange={setIsMegaOpen}
              headerOffset={headerHeight}
              triggerClassName={cn(
                'h-auto bg-transparent px-0 py-0 text-[11px] md:text-xs lg:text-sm font-semibold tracking-[0.12em] uppercase transition-colors whitespace-nowrap',
                'text-foreground hover:text-foreground/55'
              )}
            />

            <Link to="/projects" className={linkClass}>
              {tl('Projets', 'Projects', 'Progetti')}
            </Link>
          </nav>

          {/* Center logo — white on transparent over hero, dark otherwise */}
          <Link to="/" className="flex items-center justify-center px-4">
            <img
              src="/MKLogo-dark.png"
              alt="Meubles Karim"
              className="h-14 xl:h-16 w-auto object-contain"
            />
          </Link>

          {/* Right links + utilities */}
          <div className="flex items-center justify-start gap-6 xl:gap-8">
            <nav className="flex items-center gap-6 xl:gap-8">
              <Link to="/about" className={linkClass}>
                {t('header.about')}
              </Link>
              <Link to="/contact" className={linkClass}>
                {t('header.contact')}
              </Link>
            </nav>

            <div className="flex items-center gap-1 ml-2">
              <LanguageSwitcher light={false} />

              <a
                href="https://wa.me/21696275131"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('p-2 flex items-center justify-center rounded-md', iconBtnClass)}
                aria-label="WhatsApp"
              >
                <img
                  src="/icons/icons8-whatsapp.png"
                  alt="WhatsApp"
                  className="h-6 w-6 object-contain"
                />
              </a>

              <Button asChild variant="ghost" size="icon" className={iconBtnClass} aria-label="Search">
                <Link to="/search">
                  <Search size={18} />
                </Link>
              </Button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className={iconBtnClass} aria-label="Account">
                      <User size={18} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-background shadow-elevated border-border">
                    <DropdownMenuLabel>{t('header.myAccount')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile">
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('header.profile')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/appointments">
                        <Calendar className="mr-2 h-4 w-4" />
                        <span>{t('header.myAppointments')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/estimates">
                        <Wallet className="mr-2 h-4 w-4" />
                        <span>{t('header.estimates')}</span>
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin">
                            <Package className="mr-2 h-4 w-4" />
                            <span>{t('header.adminDashboard')}</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('header.signOut')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button asChild variant="ghost" size="icon" className={iconBtnClass} aria-label="Sign In">
                  <Link to="/auth">
                    <User size={18} />
                  </Link>
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                className={cn('relative', iconBtnClass)}
                aria-label="Cart"
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingCart size={18} />
                {cartItemCount > 0 && (
                  <Badge variant="brand" className="absolute -top-1 -right-1 h-5 min-w-5 justify-center px-1 rounded-full">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile / tablet bar */}
        <div className="lg:hidden flex items-center justify-between py-4">
          <Button
            variant="ghost"
            size="icon"
            className={iconBtnClass}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>

          <Link to="/" className="absolute left-1/2 -translate-x-1/2">
            <img
              src="/MKLogo-dark.png"
              alt="Meubles Karim"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={cn('relative', iconBtnClass)}
              aria-label="Cart"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingCart size={18} />
              {cartItemCount > 0 && (
                <Badge variant="brand" className="absolute -top-1 -right-1 h-5 min-w-5 justify-center px-1 rounded-full">
                  {cartItemCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background text-foreground animate-fade-in max-h-[calc(100vh-80px)] overflow-y-auto">
            <nav className="flex flex-col space-y-1 py-4 px-2">
              <div className="px-2 py-2">
                <LanguageSwitcher />
              </div>
              <Link to="/" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                {t('header.home')}
              </Link>
              <div>
                <button
                  className="w-full px-3 py-3 text-sm font-semibold uppercase tracking-wider flex items-center justify-between"
                  onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                >
                  <span>{t('header.products')}</span>
                  {isProductsDropdownOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                {isProductsDropdownOpen && (
                  <div className="ml-4 space-y-3 pb-2">
                    <Link
                      to="/products"
                      className="block px-3 py-1 text-xs font-normal uppercase tracking-wider text-muted-foreground"
                      onClick={closeMenu}
                    >
                      {tl('Tous les produits', 'All Products', 'Tutti i prodotti')}
                    </Link>
                    {categories.map((category) => (
                      <div key={category.id} className="px-3">
                        <Link
                          to={`/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="block py-1 text-xs font-bold uppercase tracking-[0.12em]"
                          onClick={closeMenu}
                        >
                          {category.name}
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/projects" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                {tl('Projets', 'Projects', 'Progetti')}
              </Link>
              <Link to="/about" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                {t('header.about')}
              </Link>
              <Link to="/contact" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                {t('header.contact')}
              </Link>
              <Link to="/search" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                {tl('Recherche', 'Search', 'Ricerca')}
              </Link>
              {user ? (
                <>
                  <Link to="/profile" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                    {t('header.profile')}
                  </Link>
                  <Link to="/appointments" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                    {t('header.myAppointments')}
                  </Link>
                  <Link to="/estimates" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                    {t('header.estimates')}
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                      {t('header.adminDashboard')}
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    className="justify-start px-3"
                    onClick={() => {
                      handleSignOut();
                      closeMenu();
                    }}
                  >
                    <LogOut size={16} className="mr-2" />
                    {t('header.signOut')}
                  </Button>
                </>
              ) : (
                <Link to="/auth" className="px-3 py-3 text-sm font-semibold uppercase tracking-wider" onClick={closeMenu}>
                  {t('header.signIn')}
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>

      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  );
};

export default Header;
